import { Box, Flex, Tabs, TabList } from "@chakra-ui/react";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useAccountData } from "../../hooks/useAccountData";
import { useTitleOnNavigation } from "../../hooks/useTitleOnNavigation";
import { UnderlinedTab } from "../../components/tabs/UnderlinedTab";
import { SearchField } from "../../components/fields/SearchField";
import { useIsSmallBreakpoint } from "../../utils/useBreakpoints";

export interface ShelfSearchConfig {
  placeholder: string;
  onSearch: (term: string) => void;
  initValue?: string;
}

export interface ShelfOutletContext {
  setSearchConfig: (config: ShelfSearchConfig | null) => void;
  setModeToggle: (node: ReactNode) => void;
}

const TABS = [
  { path: "/my-shelf/library", labelKey: "screens.shelf.tabs.library" },
  { path: "/my-shelf/purchases", labelKey: "screens.shelf.tabs.purchases" },
] as const;

export default function MyShelfScreen() {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const accountData = useAccountData();
  const isSmall = useIsSmallBreakpoint();

  const [searchConfig, setSearchConfig] = useState<ShelfSearchConfig | null>(
    null,
  );
  const [searchValue, setSearchValue] = useState("");
  const [modeToggle, setModeToggle] = useState<ReactNode>(null);

  useTitleOnNavigation(t("screens.shelf.pageTitle"));

  // Auth guard
  useEffect(() => {
    if (accountData && !accountData.user) navigate("/");
  }, [accountData]);

  // Redirect bare /my-shelf to /my-shelf/library
  useEffect(() => {
    if (
      location.pathname === "/my-shelf" ||
      location.pathname === "/my-shelf/"
    ) {
      navigate("/my-shelf/library", { replace: true });
    }
  }, [location.pathname, navigate]);

  // Reset search value when child provides new initValue
  useEffect(() => {
    setSearchValue(searchConfig?.initValue ?? "");
  }, [searchConfig?.initValue]);

  // Reset search when switching tabs (skip initial mount)
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;
      setSearchValue("");
      setSearchConfig(null);
      setModeToggle(null);
    }
  }, [location.pathname]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      searchConfig?.onSearch(value);
    },
    [searchConfig],
  );

  const activeIndex = useMemo(() => {
    const idx = TABS.findIndex((tab) =>
      location.pathname.startsWith(tab.path),
    );
    return idx >= 0 ? idx : 0;
  }, [location.pathname]);

  const outletContext = useMemo<ShelfOutletContext>(
    () => ({ setSearchConfig, setModeToggle }),
    [setSearchConfig, setModeToggle],
  );

  return (
    <Flex direction="column" width="full">
      <Flex
        direction="row"
        align="flex-end"
        justifyContent="space-between"
        bg="charcoal.charcoal"
        pl={{ base: "16px", lg: 0 }}
        pr={{ base: "16px", lg: 0 }}
        mr={{ base: 0, lg: "40px" }}
        pt="8px"
        borderBottom="1.5px solid"
        borderColor="transparent.white.20"
      >
        <Tabs
          variant="unstyled"
          index={activeIndex}
          onChange={(index) => navigate(TABS[index].path)}
        >
          <TabList gap="16px">
            {TABS.map((tab) => (
              <UnderlinedTab key={tab.path}>{t(tab.labelKey)}</UnderlinedTab>
            ))}
          </TabList>
        </Tabs>
        <Flex align="center" gap="8px" pb="8px" flexShrink={0}>
          {searchConfig && (
            <Box width={isSmall ? "140px" : "250px"}>
              <SearchField
                placeholder={searchConfig.placeholder}
                value={searchValue}
                onChange={handleSearchChange}
                onClear={() => handleSearchChange("")}
                isOpen
                clearOnBlur={false}
              />
            </Box>
          )}
          {modeToggle}
        </Flex>
      </Flex>
      <Outlet context={outletContext} />
    </Flex>
  );
}
